import { createTranslations } from '../../i18n/types';

const FAQ_KEYS = ['faq1', 'faq2', 'faq3', 'faq4', 'faq5', 'faq6'] as const;

export type FreeSkinsFaqKey = (typeof FAQ_KEYS)[number];

export const FREE_SKINS_FAQ_KEYS: readonly FreeSkinsFaqKey[] = FAQ_KEYS;

const en = {
  title: 'Free CS2 skin on SHOT',
  intro:
    'SHOT has a starter offer for new players. When you sign up, 1,500 coins land on your balance and you can open a case with them without topping up. The skin you get stays yours: you can sell it for coins or withdraw it to Steam if the conditions are met. Below is how it works and what the limits are.',
  ctaGuest: 'Sign in and get coins',
  ctaAuthed: 'Open cases',
  ctaNote: 'You need Telegram or Steam to sign in. Topping up is not required.',
  howTitle: 'How to get it',
  step1Title: 'Sign in to SHOT',
  step1Text: 'Press "Sign in" and pick Telegram or Steam. The account is created on your first sign-in.',
  step2Title: 'Get the starter coins',
  step2Text: 'Right after sign-up, 1,500 coins arrive on your balance automatically. There is nothing to activate.',
  step3Title: 'Open a case',
  step3Text:
    'In the SHOT app inside Telegram, a new player goes straight to opening the first case. On the website, open the catalog and pick a case you have enough coins for: the price is on its card.',
  step4Title: 'Decide what to do with the skin',
  step4Text: 'The skin lands in your inventory. You can sell it for coins or withdraw it to Steam.',
  termsTitle: 'Terms and limits',
  terms1:
    'Starter coins are given once, when a new account is registered. Players who are already registered do not get them again.',
  terms2:
    'What drops is unknown in advance: the item is picked at random. The starter coins do not cover every case, and items in cheaper cases are not worth much. The item list and the chance of each one are shown on the case card before you open it.',
  terms9: 'One account per person: the sign-up bonus is given only once.',
  terms3: 'To withdraw to Steam you need a Trade URL: add it in your profile settings.',
  terms4:
    'Withdrawal is open when Telegram is linked to the account or you have made at least one top-up. If you signed in with Steam, link Telegram.',
  terms5:
    'Until you have topped up, withdrawals are limited by a monthly limit on item value. The limit grows if friends you invited top up their balance. After your first top-up this limit no longer applies.',
  terms6:
    'While there have been no top-ups and your level is low, you may need to pass a "I am not a robot" check before withdrawing.',
  terms7: 'SHOT may check an account before a withdrawal. Sometimes a withdrawal is paused for a while until the check is done.',
  terms8:
    'Withdrawals can be switched off for a time, or an item can be unavailable on the market. Then the skin stays in your inventory and you can try again later.',
  useTitle: 'What you can do with the skin',
  sellTitle: 'Sell',
  sellText:
    'You can sell the skin right away, with no top-up conditions. The coins come back to your balance at the value the skin had when it dropped. You can spend them on other SHOT cases.',
  withdrawTitle: 'Withdraw to Steam',
  withdrawText:
    'The skin arrives on your Steam account as a trade offer. The conditions from the section above must be met first.',
  otherTitle: 'Other free ways',
  otherIntro: 'You can also earn coins without topping up. The amounts are small, but they add up:',
  other1:
    'Daily sign-in: 50 coins on the first day, then the reward grows for every day in a row, up to 230 coins.',
  other2: 'Linking Steam and Telegram: 400 coins for each account.',
  other3: 'Subscribing to the SHOT channel and boosting the channel in Telegram: each of these also gives coins.',
  other4:
    'Inviting friends: 6,000 coins per friend once they make their first top-up or withdraw an item. The reward is not paid if a check finds cheating.',
  seeAlso: 'See also:',
  linkCases: 'Case catalog',
  linkAchievements: 'Trophies with rewards',
  linkDraws: 'Giveaway winners',
  linkRains: 'Past drops',
  faqTitle: 'Questions and answers',
  faq1Q: 'Is the skin really free?',
  faq1A:
    'You do not need to top up: the case is opened with the starter coins SHOT gives at sign-up. The starter amount is 1,500 coins, so this is a skin from a cheap case, not a specific expensive model.',
  faq2Q: 'Do I need to top up to withdraw the skin to Steam?',
  faq2A:
    'Not necessarily, but there are conditions: a Steam Trade URL, a linked Telegram (or at least one top-up), and a monthly withdrawal limit while there have been no top-ups. See "Terms and limits" for details.',
  faq3Q: 'Can I get a free skin if I am already registered?',
  faq3A:
    'Starter coins are given only at sign-up and are not given again. You can still earn coins with the tasks from "Other free ways" and open cases with them.',
  faq4Q: 'Which skin will I get?',
  faq4A:
    'Chance decides. The items in a case and the chance of each one are shown in the catalog before you open it. We do not promise a specific skin or its price.',
  faq5Q: 'What if the skin does not withdraw?',
  faq5A:
    'The skin stays in your inventory. Check the conditions above: the Trade URL, the linked Telegram and the limit. Withdrawals can be switched off for a while and an item can be unavailable on the market, so try later or write to support.',
  faq6Q: 'Can I have several accounts?',
  faq6A:
    'No: it is one account per person, and the sign-up bonus is given only once. If SHOT sees signs of abuse, it may restrict access to the service.',
};

export default createTranslations({
  en,
  ru: {
    title: 'Бесплатный скин CS2 на SHOT',
    intro:
      'У SHOT есть стартовое предложение для новых игроков. При регистрации на баланс приходит 1 500 монет, и на них можно открыть кейс без пополнения. Скин, который выпадет, остаётся у тебя: его можно продать за монеты или вывести в Steam, если выполнены условия. Ниже — как это работает и какие есть ограничения.',
    ctaGuest: 'Войти и получить монеты',
    ctaAuthed: 'Открыть кейсы',
    ctaNote: 'Для входа нужен Telegram или Steam. Пополнять баланс не нужно.',
    howTitle: 'Как получить',
    step1Title: 'Войди в SHOT',
    step1Text: 'Нажми «Войти» и выбери Telegram или Steam. Аккаунт создаётся при первом входе.',
    step2Title: 'Получи стартовые монеты',
    step2Text: 'После регистрации на баланс автоматически приходит 1 500 монет. Ничего отдельно активировать не нужно.',
    step3Title: 'Открой кейс',
    step3Text:
      'В приложении SHOT в Telegram новый игрок сразу попадает на открытие первого кейса. На сайте открой каталог и выбери кейс, на который хватает монет: цена указана на его карточке.',
    step4Title: 'Реши, что делать со скином',
    step4Text: 'Выпавший скин попадает в инвентарь. Его можно продать за монеты или вывести в Steam.',
    termsTitle: 'Условия и ограничения',
    terms1:
      'Стартовые монеты начисляются один раз — при регистрации нового аккаунта. Тем, кто уже зарегистрирован, повторно их не дают.',
    terms2:
      'Что выпадет, заранее неизвестно: предмет выбирается случайно. Стартовых монет хватает не на любой кейс, а предметы в недорогих кейсах стоят немного. Список предметов и шанс каждого видны на карточке кейса до открытия.',
    terms9: 'Один аккаунт на человека: бонус за регистрацию даётся один раз.',
    terms3: 'Для вывода в Steam нужна Trade-ссылка: добавь её в настройках профиля.',
    terms4:
      'Вывод открыт, если к аккаунту привязан Telegram или было хотя бы одно пополнение. Если зашёл через Steam, привяжи Telegram.',
    terms5:
      'Пока пополнений не было, вывод ограничен месячным лимитом по стоимости предметов. Лимит растёт, если приглашённые друзья пополняют баланс. После первого пополнения этот лимит не действует.',
    terms6:
      'Пока пополнений не было и уровень небольшой, перед выводом может понадобиться пройти проверку «я не робот».',
    terms7: 'Перед выводом SHOT может проверить аккаунт. Иногда вывод ненадолго приостанавливают, пока идёт проверка.',
    terms8:
      'Вывод могут временно выключить, а предмет может оказаться недоступен на рынке. Тогда скин остаётся в инвентаре, и вывод можно повторить позже.',
    useTitle: 'Что можно сделать со скином',
    sellTitle: 'Продать',
    sellText:
      'Продать скин можно сразу, без условий по пополнению. На баланс вернутся монеты по стоимости скина на момент выпадения. Их можно потратить на другие кейсы SHOT.',
    withdrawTitle: 'Вывести в Steam',
    withdrawText:
      'Скин приходит на твой Steam-аккаунт предложением обмена. Для этого должны быть выполнены условия из раздела выше.',
    otherTitle: 'Другие бесплатные способы',
    otherIntro: 'Монеты можно получать и без пополнения. Суммы небольшие, но они складываются:',
    other1:
      'Ежедневный вход: 50 монет в первый день, дальше награда растёт за каждый день подряд — до 230 монет.',
    other2: 'Привязка Steam и Telegram: по 400 монет за каждый аккаунт.',
    other3: 'Подписка на канал SHOT и буст канала в Telegram: за каждое из действий тоже дают монеты.',
    other4:
      'Приглашение друзей: 6 000 монет за друга, когда он впервые пополнит баланс или выведет предмет. Если проверка находит накрутку, награду не начисляют.',
    seeAlso: 'Смотри также:',
    linkCases: 'Каталог кейсов',
    linkAchievements: 'Трофеи с наградами',
    linkDraws: 'Победители розыгрышей',
    linkRains: 'Прошедшие раздачи',
    faqTitle: 'Вопросы и ответы',
    faq1Q: 'Скин правда бесплатный?',
    faq1A:
      'Пополнять баланс не нужно: кейс открывается на стартовые монеты, которые SHOT даёт при регистрации. Стартовых монет 1 500, поэтому речь о скине из недорогого кейса, а не о конкретной дорогой модели.',
    faq2Q: 'Нужно ли пополнять баланс, чтобы вывести скин в Steam?',
    faq2A:
      'Не обязательно, но есть условия: Trade-ссылка Steam, привязанный Telegram (или хотя бы одно пополнение) и месячный лимит на вывод, пока пополнений не было. Подробнее — в разделе «Условия и ограничения».',
    faq3Q: 'Можно ли получить бесплатный скин, если я уже зарегистрирован?',
    faq3A:
      'Стартовые монеты начисляют только при регистрации, повторно их не дают. Зато монеты можно получать заданиями из раздела «Другие бесплатные способы» и открывать на них кейсы.',
    faq4Q: 'Какой скин мне выпадет?',
    faq4A:
      'Это решает случай. Какие предметы лежат в кейсе и с каким шансом они выпадают, видно в каталоге до открытия. Мы не обещаем конкретный скин или его цену.',
    faq5Q: 'Что делать, если скин не выводится?',
    faq5A:
      'Скин остаётся в инвентаре. Проверь условия выше: Trade-ссылку, привязанный Telegram и лимит. Вывод могут на время выключать, а предмет может быть недоступен на рынке — попробуй позже или напиши в поддержку.',
    faq6Q: 'Можно завести несколько аккаунтов?',
    faq6A:
      'Нет: один аккаунт на человека, а бонус за регистрацию даётся один раз. При признаках злоупотребления SHOT вправе ограничить доступ к сервису.',
  },
  // WHY: тексты страницы на остальных языках ещё не переведены — показываем английский, а title/description/h1 в реестре уже свои
  uz: { ...en, title: 'SHOTʼda CS2 uchun bepul skin' },
  fa: { ...en, title: 'اسکین رایگان CS2 در SHOT' },
  pt: { ...en, title: 'Skin de CS2 grátis no SHOT' },
  tr: { ...en, title: "SHOT'ta bedava CS2 skin" },
  uk: { ...en, title: 'Безкоштовний скін CS2 на SHOT' },
  de: { ...en, title: 'Gratis-CS2-Skin auf SHOT' },
  es: { ...en, title: 'Skin de CS2 gratis en SHOT' },
  pl: { ...en, title: 'Darmowy skin CS2 na SHOT' },
});
